import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Check, X, Sparkles, ArrowRight, ShieldCheck } from 'lucide-react';
import { PremiumButton, SectionTitle, GoldDivider } from './premium/Decor';

const PricingSection = ({ onOpenForm }) => {
  const { language } = useLanguage();
  const isHi = language === 'hi';

  const compareRows = [
    { feat: { hi: '60+ पेज की विस्तृत रिपोर्ट', en: '60+ Page Detailed Report' }, us: true, others: false },
    { feat: { hi: 'व्यक्तिगत भविष्यवाणी', en: 'Personalized Predictions' }, us: true, others: 'partial' },
    { feat: { hi: 'दशा-अंतर्दशा विश्लेषण', en: 'Dasha-Antar Dasha Analysis' }, us: true, others: false },
    { feat: { hi: '12 भाव विस्तार से', en: '12 Houses In Detail' }, us: true, others: 'partial' },
    { feat: { hi: 'न्यूमेरोलॉजी रिपोर्ट (फ्री)', en: 'Numerology Report (FREE)' }, us: true, others: false },
    { feat: { hi: 'अनुभवी ज्योतिषाचार्य', en: 'Experienced Astrologer' }, us: true, others: 'partial' },
    { feat: { hi: '48 घंटे में डिलीवरी', en: 'Delivery Within 48 hours' }, us: true, others: false },
    { feat: { hi: 'मूल्य', en: 'Price' }, us: '₹99', others: '₹500-₹5000' },
  ];

  const Cell = ({ value }) => {
    if (value === true) return <Check className="w-5 h-5 text-green-600 mx-auto" />;
    if (value === false) return <X className="w-5 h-5 text-red-500 mx-auto" />;
    if (value === 'partial')
      return <span className="text-amber-600 text-xs font-bold">{isHi ? 'सीमित' : 'Limited'}</span>;
    return <span className="text-brand-maroon font-bold">{value}</span>;
  };

  return (
    <section className="relative bg-brand-cream py-16 md:py-24 overflow-hidden">
      <div className="absolute inset-0 bg-mandala-watermark bg-repeat opacity-50 pointer-events-none" />
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] bg-mandala-large bg-no-repeat bg-center opacity-40 pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow={isHi ? 'मूल्य की तुलना' : 'Pricing Comparison'}
          title={isHi ? 'बाकी ज्योतिषियों से 95% सस्ता' : '95% Cheaper Than Other Astrologers'}
          subtitle={isHi
            ? 'जो आपको ₹5000 में मिलता है, वही गुणवत्ता हम सिर्फ ₹99 में देते हैं।'
            : "What others sell for ₹5000, we deliver at the same quality for just ₹99."}
          language={language}
        />

        {/* Comparison table */}
        <div className="mt-12 max-w-4xl mx-auto bg-white rounded-3xl border-2 border-brand-gold/30 shadow-premium overflow-hidden">
          <div className="grid grid-cols-3 bg-brand-maroon text-white">
            <div className="px-4 sm:px-6 py-4 text-sm sm:text-base font-semibold">
              {isHi ? 'विशेषताएं' : 'Features'}
            </div>
            <div className="px-2 sm:px-4 py-4 text-center font-playfair font-bold text-base sm:text-lg border-l border-white/15">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <Sparkles className="w-3.5 h-3.5 text-brand-gold-light" />
                <span>Karma Kundali</span>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-brand-gold-light">{isHi ? 'हम' : 'Us'}</div>
            </div>
            <div className="px-2 sm:px-4 py-4 text-center font-semibold border-l border-white/15">
              <div className="text-base sm:text-lg">{isHi ? 'अन्य' : 'Others'}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/70">{isHi ? 'ज्योतिषी' : 'Astrologers'}</div>
            </div>
          </div>
          {compareRows.map((row, i) => (
            <div
              key={i}
              className={`grid grid-cols-3 items-center text-sm sm:text-base ${
                i % 2 === 0 ? 'bg-white' : 'bg-brand-cream/60'
              } border-t border-brand-gold/10`}
            >
              <div className={`px-4 sm:px-6 py-4 text-brand-text-dark ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                {isHi ? row.feat.hi : row.feat.en}
              </div>
              <div className="px-2 sm:px-4 py-4 text-center border-l border-brand-gold/10">
                <Cell value={row.us} />
              </div>
              <div className="px-2 sm:px-4 py-4 text-center border-l border-brand-gold/10">
                <Cell value={row.others} />
              </div>
            </div>
          ))}
        </div>

        {/* Premium package card */}
        <div className="mt-16 max-w-2xl mx-auto">
          <div className="relative bg-gradient-to-br from-white via-brand-cream to-brand-gold-light/30 rounded-3xl border-2 border-brand-gold shadow-premium p-8 md:p-12 overflow-hidden">
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-brand-gold/15 blur-2xl" />
            <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full bg-brand-maroon/10 blur-2xl" />

            <div className="relative text-center">
              <div className="inline-flex items-center gap-1.5 bg-brand-maroon text-white text-xs font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full mb-5">
                <Sparkles className="w-3 h-3 text-brand-gold-light" />
                <span>{isHi ? 'सीमित समय का ऑफर' : 'Limited Time Offer'}</span>
              </div>
              <h3 className={`font-playfair font-bold text-2xl md:text-3xl text-brand-maroon mb-2 ${isHi ? 'font-hindi-serif' : ''}`}>
                {isHi ? 'पर्सनलाइज़्ड कर्मा कुंडली' : 'Personalized Karma Kundali'}
              </h3>
              <p className={`text-brand-text-soft text-sm mb-6 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                {isHi ? '+ निःशुल्क न्यूमेरोलॉजी रिपोर्ट' : '+ Free Numerology Report'}
              </p>

              <div className="flex items-baseline justify-center gap-3 mb-3">
                <span className="text-brand-text-mute line-through text-2xl font-outfit">₹999</span>
                <span className="font-playfair font-black text-6xl md:text-7xl text-brand-maroon">₹99</span>
              </div>
              <p className={`text-xs text-brand-text-mute mb-6 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                {isHi ? 'कुंडली ₹11 + न्यूमेरोलॉजी ₹10' : 'Kundli ₹11 + Numerology ₹10'}
              </p>

              <GoldDivider />

              <ul className="text-left max-w-md mx-auto space-y-2.5 my-6">
                {[
                  { hi: '60+ पेज की विस्तृत रिपोर्ट', en: '60+ pages detailed report' },
                  { hi: '12 भाव और दशा विश्लेषण', en: '12 houses & dasha analysis' },
                  { hi: 'व्यक्तिगत उपाय और मंत्र', en: 'Personal remedies & mantras' },
                  { hi: 'प्रीमियम न्यूमेरोलॉजी रिपोर्ट (फ्री)', en: 'Premium Numerology report (FREE)' },
                  { hi: '48 घंटे में email डिलीवरी', en: 'Email delivery within 48 hours' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Check className="w-5 h-5 text-brand-gold-dark flex-shrink-0 mt-0.5" />
                    <span className={`text-brand-text-dark text-sm md:text-base ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                      {isHi ? item.hi : item.en}
                    </span>
                  </li>
                ))}
              </ul>

              <PremiumButton size="lg" onClick={onOpenForm} className="w-full sm:w-auto">
                <Sparkles className="w-4 h-4" />
                <span className={isHi ? 'font-hindi' : 'font-outfit'}>
                  {isHi ? 'अभी ऑर्डर करें — सिर्फ ₹99' : 'Order Now — Only ₹99'}
                </span>
                <ArrowRight className="w-4 h-4" />
              </PremiumButton>

              <div className="flex items-center justify-center gap-1.5 mt-5 text-xs text-brand-text-mute">
                <ShieldCheck className="w-3.5 h-3.5 text-green-600" />
                <span className={isHi ? 'font-hindi' : 'font-outfit'}>
                  {isHi ? '100% सुरक्षित Razorpay भुगतान' : '100% Secure Razorpay Payment'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
